import {
  MessageDetailsNotificationDTO,
  MessageDirection,
  MessageNotificationDTO,
} from "@/hooks/types"
import { extractErrorMessage } from "@/lib/error"
import { trackEvent } from "@/lib/tracking/analytics"
import { authStore } from "@/store/auth"
import { AxiosError } from "axios"
import { toast } from "sonner"
import { create } from "zustand"

import { markConversationAsClosed } from "../actions/conversation-actions"
import { channelConversationService } from "../services/channel-conversation-service"
import { contactService } from "../services/contact-service"
import { feedbackService, type FeedbackResponse } from "../services/feedback-service"
import { leadService } from "../services/lead-service"
import { WebSocketEvent } from "../services/websocket-service"
import { MessageContent } from "../types"
import { toggleNoteReaction, toNoteReactions } from "../utils/reactions"

export type MessageType =
  | "text"
  | "image"
  | "audio"
  | "myaudio"
  | "video"
  | "document"
  | "sticker"
  | "location"
  | "reply"
  | "forward"
  | "system"
  | "notification"
  | "template"
  | "interactive"
  | "pix_button"
  | "request_payment"
  | "internal_note"

export type ConversationStatus = "waiting" | "active" | "closed" | "all"

// Outbound WhatsApp interactive message (native buttons/list/poll). Mirrors the
// backend content for message_type="interactive".
export interface InteractiveContent {
  menuType: "button" | "list" | "poll"
  text: string
  choices: string[]
  buttonText?: string
  footerText?: string
}

// Inbound reply where the lead tapped a button or picked a list option. The
// backend persists it as a "text" message carrying the selected option fields.
export interface ButtonReplyInfo {
  selectedTitle?: string
  selectedId?: string
  selectedButtonId?: string
}

export interface MessageMedia {
  url: string
  type: "image" | "audio" | "video" | "document" | "myaudio" | "sticker"
  name?: string
  size?: number
  duration?: number
  thumbnail?: string
  caption?: string
  filename?: string
  mimeType?: string
  width?: number
  height?: number
}

export interface MessageLocation {
  latitude: number
  longitude: number
  name?: string
  address?: string
}

export interface MessageEditHistoryEntry {
  content: Record<string, unknown>
  edited_at?: string | null
}

export interface MessageMetadata {
  // Edit/revoke fields (populated by backend when a sender edits or
  // deletes a previously sent WhatsApp message). See speeke-api
  // Message.apply_edit / apply_revoke.
  edited_at?: string
  deleted?: boolean
  deleted_at?: string
  edit_history?: MessageEditHistoryEntry[]
  // True for WhatsApp ephemeral (auto-delete) messages.
  ephemeral?: boolean
  // Reactions keyed by emoji. Inbound WhatsApp reactions on regular messages
  // arrive as a count {emoji: number}; internal-note reactions track the
  // reactor user ids {emoji: string[]} so the UI can show who reacted and let
  // each user toggle their own reaction.
  reactions?: Record<string, number | string[]>
  // Inbound-pipeline status (per the pipeline_status_enabled feature). Set from
  // the pipeline run that processed this message, so the UI can surface a
  // failed/in-progress agent turn and offer a retry.
  pipeline_status?: "in_progress" | "completed" | "failed"
  pipeline_run_id?: string
  failed_step?: string
  // Attendant user ids mentioned in an internal note (server-controlled). Used
  // to resolve and highlight the "@Name" tokens embedded in the note text. No
  // notification is fired for a mention.
  mentioned_user_ids?: string[]
  // Free-form passthrough for any other backend-set metadata.
  [key: string]: any
}

export interface Message {
  id: string
  content: string | any
  formatted_content: string // Always a safe string for rendering
  sender: "user" | "contact" | "agent"
  timestamp: Date
  status: "sent" | "delivered" | "read" | "pending" | "failed"
  type: MessageType
  media?: MessageMedia
  location?: MessageLocation
  replyTo?: {
    id: string
    content: string
    formatted_content: string
    sender: "user" | "contact" | "agent"
    type?: MessageType
    media?: MessageMedia
    // Optional: when backend includes the quoted message's metadata, allow
    // the UI to reflect e.g. deleted/edited state of the original message.
    metadata?: MessageMetadata
  }
  forwarded?: boolean
  forwardedFrom?: string
  isAIMessage?: boolean
  // True when message_type is "internal_note": an attendant-only note persisted
  // in the timeline but never dispatched to the channel or seen by the customer.
  isInternalNote?: boolean
  // Raw author user id (the message sender on the backend). For an internal note
  // this is the authoring attendant's user id, compared against the current user
  // to gate the author-only edit action.
  authorId?: string
  langfuseTraceId?: string | null
  metadata?: MessageMetadata
  // Present on outbound native interactive messages (buttons/list/poll).
  interactive?: InteractiveContent
  // Present on inbound messages that are a tap on a previous button/list.
  buttonReply?: ButtonReplyInfo
}

export interface Chat {
  id: string
  channelId: string // Channel ID this conversation belongs to
  contactId: string
  leadId: string
  contactName: string
  phoneNumber?: string
  lastMessage?: Message
  unreadCount: number
  messages: Message[]
  online: boolean
  imageUrl?: string
  isAIHandled?: boolean
  handoffUntilDate?: Date
  handoffReason?: string | null
  messagingWindowExpiresAt?: Date
  status?: "waiting" | "active" | "closed"
  isBlocked?: boolean
  blockReason?: string | null
  isGroup?: boolean
  groupId?: string | null
  groupJid?: string | null
  metadata?: Record<string, any>
  tags?: string[]
  attendantId?: string | null
}

export interface Contact {
  id: string
  name: string
  email: string
  phone: string
  imageUrl: string
  is_blocked?: boolean
  block_reason?: string | null
  blocked_at?: string | null
}

// API Interfaces
export interface ApiHandoff {
  started_at: string
  until_date: string | null
  reason: string | null
}

// WebSocket Notification DTO for handoff events
export interface HandoffNotificationDTO {
  conversation_id: string
  handoff_started_at: string | null
  handoff_until_date: string | null
  handoff_reason: string | null
  is_active: boolean
  /** Conversation status after the handoff change; older backends omit it. */
  status?: "waiting" | "active" | "closed" | null
}

// WebSocket Notification DTO for conversation status changes (flat payload
// from the backend's ConversationUpdatedEvent, not a full conversation)
export interface ConversationUpdateNotificationDTO {
  id: string
  organization_id?: string
  status: "waiting" | "active" | "closed"
  attendant_id?: string | null
}

export interface ApiConversation {
  id: string
  channelId: string // Channel ID this conversation belongs to
  contactId: string
  leadId: string
  contactName: string
  phoneNumber?: string
  unreadCount: number
  imageUrl: string
  online: boolean
  isAIHandled: boolean
  handoff: ApiHandoff | null // Nested handoff object, null when not active
  messagingWindowExpiresAt: string | null // ISO datetime for Instagram/Messenger
  status: "waiting" | "active" | "closed"
  isBlocked?: boolean
  blockReason?: string | null
  isGroup?: boolean
  groupId?: string | null
  groupJid?: string | null
  metadata?: Record<string, any>
  leadTags?: string[]
  attendantId?: string | null
  lastMessage: {
    id: string
    content: string
    sender: string
    timestamp: string
    status: string
    type: string
    is_ai_message?: boolean
    media?: {
      url: string
      type: string
      duration: number
      filename: string
    }
  }
}

export interface ApiContact {
  id: string
  organization_id: string
  phone_number: string
  name: string | null
  profile_name: string | null
  profile_image_url: string | null
  is_business: boolean
  created_at: string
}

export interface ApiMessage {
  id: string
  conversation_id: string
  direction: "inbound" | "outbound"
  message_type: string
  sender: string
  recipient: string
  content: any
  status: "pending" | "sent" | "delivered" | "read" | "failed"
  created_at: string
  is_ai_message: boolean
  trace_id?: string | null
  quoted_message_id?: string | null
  is_forwarded?: boolean
  metadata?: {
    langfuse_trace_id?: string | null
    tool_calls?: Array<{
      id: string
      function: {
        name: string
        arguments: string
      }
    }>
    sender_phone?: string | null
    sender_name?: string | null
    sender_photo_url?: string | null
  } | null
}

export interface Lead {
  id: string
  organization_id: string
  user_id: string
  name: string
  email: string
  phone_number: string
  address: string | null
  status:
    | "new"
    | "contacted"
    | "booked"
    | "confirmed"
    | "attended"
    | "cancelled"
    | "no_show"
    | "follow_up"
    | "converted"
    | "lost"
    | "completed"
    | string
  is_blocked?: boolean
  block_reason?: string | null
  blocked_at?: string | null
  source: string | null
  tags: string[]
  first_contact_at: string | null
  last_contact_at: string | null
  next_appointment_at?: string | null
  last_appointment_at?: string | null
  photo_url?: string | null
  contact_id?: string | null
  contacted_at?: string | null
  booked_at?: string | null
  confirmed_at?: string | null
  attended_at?: string | null
  cancelled_at?: string | null
  no_show_at?: string | null
  follow_up_at?: string | null
  converted_at?: string | null
  last_appointment_id?: string | null
  notes?: string | null
  lead_metadata?: Record<string, any> | null
  custom_attributes?: Record<string, string> | null
  attribute_group_ids?: string[] | null
}

interface ChatState {
  chats: Chat[]
  chatsByStatus: {
    waiting: Chat[]
    active: Chat[]
    closed: Chat[]
    all: Chat[]
  }
  stats: {
    waiting: number
    active: number
    closed: number
    all: number
    // Conversations with unread inbound messages, per status.
    unread?: {
      waiting: number
      active: number
      closed: number
      all: number
    }
  }
  chatsLoading: boolean
  statusFilter: ConversationStatus
  setStatusFilter: (status: ConversationStatus, options?: { autoSelect?: boolean }) => Promise<void>
  activeChat: string | null
  loading: boolean
  error: string | null
  messagesLoading: boolean
  messagesError: string | null
  hasMore: boolean
  loadingMore: boolean
  loadMoreConversations: () => Promise<void>
  setActiveChat: (id: string | null | undefined) => void
  sendMessage: (
    chatId: string,
    content: MessageContent,
    options?: {
      replyTo?: string
      forwarded?: boolean
      forwardedFrom?: string
      isInternalNote?: boolean
      mentionedUserIds?: string[]
    }
  ) => void
  /**
   * Edits an internal note (author only, enforced server-side). Applies an
   * optimistic content/edited-at update, then persists via PATCH and relies on
   * the realtime message.update to reconcile. Reverts on failure.
   */
  editMessage: (messageId: string, text: string, mentionedUserIds?: string[]) => Promise<void>
  /**
   * Soft-deletes an internal note (author or admin, enforced server-side).
   * Applies an optimistic deleted-tombstone, then persists via DELETE and
   * relies on the realtime message.update. Reverts on failure.
   */
  deleteMessage: (messageId: string) => Promise<void>
  /**
   * Toggles the current user's emoji reaction on an internal note. Applies an
   * optimistic reaction update, then persists via POST and relies on the
   * realtime message.update to reconcile. Reverts on failure.
   */
  toggleReaction: (messageId: string, emoji: string, userId: string) => Promise<void>
  markAsRead: (chatId: string) => void
  fetchConversations: (status?: ConversationStatus) => Promise<void>
  syncCounts: () => Promise<void>
  reconcileAfterReconnect: () => Promise<void>
  fetchConversation: (conversationId: string) => Promise<Chat>
  fetchMessages: (conversationId: string) => Promise<void>
  handleWebSocketEvent: (event: WebSocketEvent) => void
  handleNewMessage: (data: MessageNotificationDTO) => void
  handleHandoffUpdate: (data: HandoffNotificationDTO) => void
  handleConversationUpdate: (data: ConversationUpdateNotificationDTO) => void
  /**
   * Applies a status transition to a loaded conversation: patches it, removes
   * it from the visible list when it no longer matches the current tab (the
   * open conversation is kept so the pane never blanks), pulls it into the
   * list when it starts matching, and re-syncs the tab badges.
   */
  applyConversationStatusChange: (
    conversationId: string,
    newStatus: "waiting" | "active" | "closed",
    patch?: Partial<Chat>
  ) => void
  endHandoff: (conversationId: string) => Promise<void>
  contact: Contact | null
  contactLoading: boolean
  contactError: string | null
  fetchContact: (contactId: string) => Promise<void>
  lead: Lead | null
  leadLoading: boolean
  leadError: string | null
  fetchLead: (leadId: string) => Promise<void>
  markConversationAsClosed: (conversationId: string) => Promise<void>
  feedbacksByMessageId: Record<string, FeedbackResponse>
  fetchFeedbacks: (messageIds: string[]) => Promise<void>
  setFeedback: (messageId: string, feedback: FeedbackResponse | null) => void
  hasMoreMessages: Record<string, boolean>
  loadingMoreMessages: boolean
  loadMoreMessages: (conversationId: string) => Promise<void>
  historySyncStatus: Record<
    string,
    "idle" | "requesting" | "waiting" | "completed" | "timeout" | "no_more"
  >
  requestWhatsAppHistory: (conversationId: string) => Promise<void>
  fetchHistorySyncStatus: (conversationId: string) => Promise<void>
  searchResults: Chat[] | null
  searchLoading: boolean
  searchConversations: (query: string) => Promise<void>
  clearSearch: () => void
  tagsInclude: string[]
  tagsExclude: string[]
  addTagInclude: (tag: string) => Promise<void>
  removeTagInclude: (tag: string) => Promise<void>
  addTagExclude: (tag: string) => Promise<void>
  removeTagExclude: (tag: string) => Promise<void>
  clearTagFilters: () => Promise<void>
  updateChatTags: (leadId: string, tags: string[]) => void
  groupFilter: "all" | "group" | "direct"
  setGroupFilter: (filter: "all" | "group" | "direct") => Promise<void>
  channelFilter: string[]
  setChannelFilter: (channelIds: string[]) => Promise<void>
  /** When false (default), conversations from disconnected/removed channels are hidden. */
  showDisconnectedChannels: boolean
  setShowDisconnectedChannels: (value: boolean) => Promise<void>
  /** Ids of channels currently connected. null while channels are still loading. */
  connectedChannelIds: string[] | null
  setConnectedChannelIds: (channelIds: string[]) => Promise<void>
  /** How many conversations belong to disconnected channels, per status tab. */
  disconnectedCountByStatus: { waiting: number; active: number; closed: number; all: number }
  assigneeFilter: AssigneeFilter
  setAssigneeFilter: (filter: AssigneeFilter) => Promise<void>
  assignAttendant: (conversationId: string, attendantId: string | null) => Promise<void>
}

export interface AssigneeFilter {
  /** When true, the API resolves the filter to the caller's user id server-side. */
  assignedToMe: boolean
  /** When true, conversations with no attendant_id are included. */
  includeUnassigned: boolean
  /** Specific attendant ids to filter by. Empty list means no per-attendant filter. */
  attendantIds: string[]
}

// Helper function to map API conversation to our Chat model
const mapApiConversationToChat = (apiConversation: ApiConversation): Chat => {
  return {
    id: apiConversation.id,
    channelId: apiConversation.channelId,
    contactId: apiConversation.contactId,
    leadId: apiConversation.leadId,
    contactName: apiConversation.contactName,
    phoneNumber: apiConversation.phoneNumber,
    unreadCount: apiConversation.unreadCount,
    online: apiConversation.online,
    isAIHandled: apiConversation.isAIHandled,
    // If handoff is present (not null), it means handoff is active (backend already checked expiration)
    handoffUntilDate: apiConversation.handoff?.until_date
      ? new Date(apiConversation.handoff.until_date)
      : undefined,
    handoffReason: apiConversation.handoff?.reason ?? undefined,
    messagingWindowExpiresAt: apiConversation.messagingWindowExpiresAt
      ? new Date(apiConversation.messagingWindowExpiresAt)
      : undefined,
    status: apiConversation.status,
    messages: [],
    imageUrl: apiConversation.imageUrl,
    isBlocked: apiConversation.isBlocked,
    blockReason: apiConversation.blockReason,
    isGroup: apiConversation.isGroup ?? false,
    groupId: apiConversation.groupId ?? null,
    groupJid: apiConversation.groupJid ?? null,
    metadata: apiConversation.metadata,
    tags: apiConversation.leadTags ?? [],
    attendantId: apiConversation.attendantId ?? null,
    lastMessage: apiConversation.lastMessage
      ? {
          id: apiConversation.lastMessage.id,
          content: apiConversation.lastMessage.content,
          formatted_content: extractFormattedContent(apiConversation.lastMessage.content),
          sender:
            apiConversation.lastMessage.sender === "user"
              ? "user"
              : apiConversation.lastMessage.sender === "agent"
                ? "agent"
                : "contact",
          timestamp: new Date(apiConversation.lastMessage.timestamp),
          status: apiConversation.lastMessage.status as "sent" | "delivered" | "read" | "pending",
          type: apiConversation.lastMessage.type as MessageType,
          isAIMessage: apiConversation.lastMessage.is_ai_message ?? false,
          media: apiConversation.lastMessage.media
            ? {
                url: apiConversation.lastMessage.media.url,
                type: apiConversation.lastMessage.media.type as
                  | "image"
                  | "audio"
                  | "video"
                  | "document",
                duration: apiConversation.lastMessage.media.duration,
                name: apiConversation.lastMessage.media.filename,
              }
            : undefined,
        }
      : undefined,
  }
}

// Helper function to normalize message content
// Ensures content is always a valid string or preserved object for system messages
const normalizeMessageContent = (
  content: any,
  messageType: string
): string | Record<string, any> => {
  // For system, notification, and payment messages, preserve the full content object
  if (
    messageType === "system" ||
    messageType === "notification" ||
    messageType === "pix_button" ||
    messageType === "request_payment" ||
    messageType === "template" ||
    messageType === "interactive"
  ) {
    return content || {}
  }

  // For all other messages, ensure we have a string
  if (typeof content === "string") {
    return content
  }

  // If content is an object, try to extract text
  if (content && typeof content === "object") {
    return content.text || content.caption || ""
  }

  // Fallback to empty string
  return ""
}

// Helper function to extract safe string content for rendering
const extractFormattedContent = (content: any): string => {
  if (typeof content === "string") {
    return content
  }

  if (content && typeof content === "object") {
    return content.text || content.caption || ""
  }

  return ""
}

// Helper function to map API message to our Message model
export const mapApiMessageToMessage = (apiMessage: ApiMessage): Message => {
  const content = normalizeMessageContent(apiMessage.content, apiMessage.message_type)
  const formatted_content = extractFormattedContent(apiMessage.content)
  const rawContent = apiMessage.content

  const interactive =
    apiMessage.message_type === "interactive" && rawContent && typeof rawContent === "object"
      ? {
          menuType: rawContent.menu_type || "button",
          text: rawContent.text || "",
          choices: Array.isArray(rawContent.choices) ? rawContent.choices : [],
          buttonText: rawContent.button_text || undefined,
          footerText: rawContent.footer_text || undefined,
        }
      : undefined

  const hasButtonReply =
    apiMessage.message_type !== "interactive" &&
    rawContent &&
    typeof rawContent === "object" &&
    (rawContent.selected_title || rawContent.selected_id || rawContent.selected_button_id)
  const buttonReply = hasButtonReply
    ? {
        selectedTitle: rawContent.selected_title || undefined,
        selectedId: rawContent.selected_id || undefined,
        selectedButtonId: rawContent.selected_button_id || undefined,
      }
    : undefined

  return {
    interactive,
    buttonReply,
    id: apiMessage.id,
    content: content,
    formatted_content: formatted_content,
    media: apiMessage.content?.url
      ? {
          url: apiMessage.content.url,
          type: apiMessage.content.type || apiMessage.message_type,
          name: apiMessage.content.name,
          size: apiMessage.content.file_length || apiMessage.content.size,
          duration: apiMessage.content.duration,
          thumbnail: apiMessage.content.thumbnail,
          caption: apiMessage.content.caption,
          filename: apiMessage.content.filename,
          mimeType: apiMessage.content.mime_type,
          width: apiMessage.content.width,
          height: apiMessage.content.height,
        }
      : undefined,
    location:
      apiMessage.message_type === "location"
        ? {
            latitude: apiMessage.content?.latitude ?? 0,
            longitude: apiMessage.content?.longitude ?? 0,
            name: apiMessage.content?.name ?? "",
            address: apiMessage.content?.address ?? "",
          }
        : undefined,
    sender: apiMessage.direction === "inbound" ? "contact" : "user",
    timestamp: new Date(apiMessage.created_at),
    status: apiMessage.status,
    type: apiMessage.message_type as MessageType,
    isAIMessage: apiMessage.is_ai_message,
    isInternalNote: apiMessage.message_type === "internal_note",
    authorId: apiMessage.sender,
    langfuseTraceId: apiMessage.metadata?.langfuse_trace_id || null,
    metadata: apiMessage.metadata || undefined,
    forwarded: apiMessage.is_forwarded || false,
  }
}

// Per-conversation buffer for chunked `history.sync_complete` events.
// Backend chunks payloads larger than ~60KB so they fit under Ably's 64KB
// hard limit (SPEEKE-API-X4). The buffer is committed to store state only
// when the final chunk (`is_last=true`) arrives.
const historySyncChunkBuffer = new Map<string, ApiMessage[][]>()

const resolveQuotedMessages = (messages: Message[], apiMessages: ApiMessage[]): Message[] => {
  const messageMap = new Map(messages.map(m => [m.id, m]))
  return messages.map((msg, idx) => {
    const apiMsg = apiMessages[idx]
    if (apiMsg?.quoted_message_id) {
      const quoted = messageMap.get(apiMsg.quoted_message_id)
      if (quoted) {
        return {
          ...msg,
          replyTo: {
            id: quoted.id,
            content: quoted.content,
            formatted_content: quoted.formatted_content,
            sender: quoted.sender,
            type: quoted.type,
            media: quoted.media,
          },
        }
      }
    }
    return msg
  })
}

const mapMessageNotificationToMessage = (message: MessageDetailsNotificationDTO): Message => {
  const content = normalizeMessageContent(message.content, message.message_type)
  const formatted_content = extractFormattedContent(message.content)

  return {
    id: message.id,
    content: content,
    formatted_content: formatted_content,
    media: message.content?.url
      ? {
          url: message.content.url,
          type: message.content.type,
          name: message.content.name,
          size: message.content.size,
          duration: message.content.duration,
          thumbnail: message.content.thumbnail,
          caption: message.content.caption,
          filename: message.content.filename,
        }
      : undefined,
    location:
      message.message_type === "location"
        ? {
            latitude: message.content?.latitude ?? 0,
            longitude: message.content?.longitude ?? 0,
            name: message.content?.name ?? "",
            address: message.content?.address ?? "",
          }
        : undefined,
    sender: message.direction === MessageDirection.OUTBOUND ? "user" : "contact",
    timestamp: new Date(message.created_at),
    status: message.status as "sent" | "delivered" | "read" | "pending" | "failed",
    type: message.message_type as MessageType,
    isAIMessage: message.is_ai_message,
    isInternalNote: (message.message_type as string) === "internal_note",
    // Author id comes from the realtime payload's `sender` field (same field
    // the load path reads), falling back to the sender_id metadata only when an
    // older backend omits it. Without this the author would not see the
    // edit/delete affordances on a note that arrived live until a refetch.
    authorId: message.sender ?? message.metadata?.sender_id ?? undefined,
    langfuseTraceId: message.metadata?.langfuse_trace_id || null,
    metadata: message.metadata || undefined,
    forwarded: message.is_forwarded || false,
  }
}

const SHOW_DISCONNECTED_CHANNELS_KEY = "speeke:chat:showDisconnectedChannels"

const readShowDisconnectedChannels = (): boolean => {
  try {
    return localStorage.getItem(SHOW_DISCONNECTED_CHANNELS_KEY) === "true"
  } catch {
    return false
  }
}

const persistShowDisconnectedChannels = (value: boolean): void => {
  try {
    localStorage.setItem(SHOW_DISCONNECTED_CHANNELS_KEY, value ? "true" : "false")
  } catch {
    // Storage unavailable (private mode etc.) - keep the in-memory value only.
  }
}

// Resolve which channel_ids to send to the conversations API. An explicit
// channel filter chosen in the UI always wins. Otherwise, unless the user opted
// to show disconnected channels, restrict to the connected ones. `blockAll`
// means "no connected channels while hiding": there is nothing to show, and we
// must skip the request (an empty channel_ids list would be read as no filter).
const resolveFetchChannelIds = (
  channelFilter: string[],
  showDisconnectedChannels: boolean,
  connectedChannelIds: string[] | null
): { ids?: string[]; blockAll?: boolean } => {
  if (channelFilter.length) return { ids: channelFilter }
  if (showDisconnectedChannels) return {}
  if (connectedChannelIds === null) return {}
  if (connectedChannelIds.length === 0) return { blockAll: true }
  return { ids: connectedChannelIds }
}

export const useChatStore = create<ChatState>()((set, get) => ({
  chats: [],
  chatsByStatus: {
    waiting: [],
    active: [],
    closed: [],
    all: [],
  },
  stats: {
    waiting: 0,
    active: 0,
    closed: 0,
    all: 0,
  },
  activeChat: null,
  loading: false,
  chatsLoading: false,
  error: null,
  messagesLoading: false,
  messagesError: null,
  hasMore: true,
  loadingMore: false,
  statusFilter: "waiting",
  contact: null,
  contactLoading: false,
  contactError: null,
  lead: null,
  leadLoading: false,
  leadError: null,
  feedbacksByMessageId: {},
  hasMoreMessages: {},
  loadingMoreMessages: false,
  historySyncStatus: {},
  searchResults: null,
  searchLoading: false,
  tagsInclude: [],
  tagsExclude: [],
  groupFilter: "all",
  channelFilter: [],
  showDisconnectedChannels: readShowDisconnectedChannels(),
  connectedChannelIds: null,
  disconnectedCountByStatus: { waiting: 0, active: 0, closed: 0, all: 0 },
  assigneeFilter: { assignedToMe: false, includeUnassigned: false, attendantIds: [] },

  setStatusFilter: async (status: ConversationStatus, { autoSelect = true } = {}) => {
    set({ statusFilter: status, chats: [], hasMore: true, activeChat: null })
    get().syncCounts()
    await get().fetchConversations(status)

    // Auto-select first chat and load its messages (skip on mobile to show list first)
    if (autoSelect) {
      const { chats, setActiveChat } = get()
      if (chats.length > 0) {
        setActiveChat(chats[0].id)
      }
    }
  },

  setActiveChat: (id: string | null | undefined) => {
    set({ activeChat: id, lead: null, leadLoading: false, leadError: null })

    if (id) {
      let chat = get().chats.find(c => c.id === id)

      // If not in chats, check searchResults and inject into chats so messages/send work
      if (!chat) {
        const searchChat = get().searchResults?.find(c => c.id === id)
        if (searchChat) {
          chat = searchChat
          set(state => ({ chats: [searchChat, ...state.chats] }))
        }
      }

      if (chat) {
        if (chat.messages.length === 0 || get().hasMoreMessages[id] === undefined) {
          get().fetchMessages(id)
        }
        if (chat.leadId) {
          get().fetchLead(chat.leadId)
        } else if (chat.phoneNumber) {
          set({ leadLoading: true, leadError: null })
          leadService
            .findLeads(chat.phoneNumber)
            .then(leads => {
              // Drop the result if the operator switched conversations while the
              // lookup was in flight, so the panel never shows another chat's lead.
              if (get().activeChat !== id) return
              if (leads.length > 0) {
                const lead = leads[0]
                set({ lead, leadLoading: false })
              } else {
                set({ leadError: "Lead não encontrado no CRM.", leadLoading: false })
              }
            })
            .catch(err => {
              if (get().activeChat !== id) return
              console.error("Error finding lead by phone:", err)
              set({ leadError: "Erro ao buscar informações do Lead.", leadLoading: false })
            })
        } else {
          console.warn(`Chat with ID ${id} does not have a leadId.`)
          set({ leadError: "Lead ID não encontrado para esta conversa.", leadLoading: false })
        }
      }
    } else {
      set({ lead: null })
    }
  },

  fetchConversations: async (status?: ConversationStatus) => {
    try {
      set({ chatsLoading: true, error: null })

      const {
        tagsInclude,
        tagsExclude,
        groupFilter,
        channelFilter,
        assigneeFilter,
        showDisconnectedChannels,
        connectedChannelIds,
      } = get()
      const { ids, blockAll } = resolveFetchChannelIds(
        channelFilter,
        showDisconnectedChannels,
        connectedChannelIds
      )
      if (blockAll) {
        set(state => ({
          chats: [],
          chatsByStatus: { ...state.chatsByStatus, [status || "all"]: [] },
          hasMore: false,
        }))
        return
      }
      const isGroupParam =
        groupFilter === "group" ? true : groupFilter === "direct" ? false : undefined
      const limit = 20
      const conversations = await channelConversationService.getConversations(
        status || "all",
        limit,
        0,
        undefined,
        tagsInclude.length ? tagsInclude : undefined,
        tagsExclude.length ? tagsExclude : undefined,
        isGroupParam,
        ids?.length ? ids : undefined,
        assigneeFilter.attendantIds.length ? assigneeFilter.attendantIds : undefined,
        assigneeFilter.includeUnassigned || undefined,
        assigneeFilter.assignedToMe || undefined
      )
      if (Array.isArray(conversations)) {
        // Handoff info now comes directly from the API response
        const chats = conversations.map(mapApiConversationToChat)

        // Update specific status list and current view
        set(state => ({
          chats: chats,
          chatsByStatus: {
            ...state.chatsByStatus,
            [status || "all"]: chats,
          },
          hasMore: conversations.length >= limit,
        }))
      }
    } catch (error) {
      console.error(error)
      const errorMessage = extractErrorMessage(error, "Falha ao buscar conversas")
      set({ error: errorMessage })
    } finally {
      set({ chatsLoading: false })
    }
  },

  loadMoreConversations: async () => {
    const {
      statusFilter,
      chats,
      loadingMore,
      hasMore,
      tagsInclude,
      tagsExclude,
      groupFilter,
      channelFilter,
      assigneeFilter,
      showDisconnectedChannels,
      connectedChannelIds,
    } = get()
    if (loadingMore || !hasMore) return

    const { ids, blockAll } = resolveFetchChannelIds(
      channelFilter,
      showDisconnectedChannels,
      connectedChannelIds
    )
    if (blockAll) {
      set({ hasMore: false })
      return
    }

    try {
      set({ loadingMore: true })
      const isGroupParam =
        groupFilter === "group" ? true : groupFilter === "direct" ? false : undefined
      const limit = 20
      const offset = chats.length
      const conversations = await channelConversationService.getConversations(
        statusFilter,
        limit,
        offset,
        undefined,
        tagsInclude.length ? tagsInclude : undefined,
        tagsExclude.length ? tagsExclude : undefined,
        isGroupParam,
        ids?.length ? ids : undefined,
        assigneeFilter.attendantIds.length ? assigneeFilter.attendantIds : undefined,
        assigneeFilter.includeUnassigned || undefined,
        assigneeFilter.assignedToMe || undefined
      )

      if (Array.isArray(conversations)) {
        // Handoff info now comes directly from the API response
        const newChats = conversations.map(mapApiConversationToChat)

        if (newChats.length < limit) {
          set({ hasMore: false })
        }

        if (newChats.length > 0) {
          set(state => ({
            chats: [...state.chats, ...newChats],
            chatsByStatus: {
              ...state.chatsByStatus,
              [statusFilter]: [...(state.chatsByStatus[statusFilter] || []), ...newChats],
            },
          }))
        }
      }
    } catch (error) {
      console.error("Failed to load more conversations", error)
    } finally {
      set({ loadingMore: false })
    }
  },

  syncCounts: async () => {
    try {
      const {
        connectedChannelIds,
        showDisconnectedChannels,
        channelFilter,
        tagsInclude,
        tagsExclude,
        groupFilter,
        assigneeFilter,
      } = get()
      const channelsKnown = connectedChannelIds !== null

      // The badges must count exactly what the list shows, so both requests
      // carry the same tag/group/assignee filters the list request sends.
      const sharedFilters = {
        tagsInclude: tagsInclude.length ? tagsInclude : undefined,
        tagsExclude: tagsExclude.length ? tagsExclude : undefined,
        isGroup: groupFilter === "group" ? true : groupFilter === "direct" ? false : undefined,
        attendantIds: assigneeFilter.attendantIds.length ? assigneeFilter.attendantIds : undefined,
        includeUnassigned: assigneeFilter.includeUnassigned || undefined,
        assignedToMe: assigneeFilter.assignedToMe || undefined,
      }

      const zeroCounts = { waiting: 0, active: 0, closed: 0, all: 0 }

      // An explicit channel filter wins over the connected/disconnected split,
      // mirroring resolveFetchChannelIds for the list.
      if (channelFilter.length) {
        const filteredCounts = await channelConversationService.getConversationCounts({
          ...sharedFilters,
          channelIds: channelFilter,
        })
        set({ stats: filteredCounts, disconnectedCountByStatus: zeroCounts })
        return
      }

      // Server-side counts: real totals, not the size of one paginated page.
      const totalCounts = await channelConversationService.getConversationCounts(sharedFilters)

      // Counts restricted to connected channels. An empty connected set means
      // nothing is visible, so we skip the call (an empty channel_ids list would
      // be read as "no filter" by the API) and treat connected counts as zero.
      let connectedCounts = totalCounts
      if (channelsKnown) {
        connectedCounts =
          connectedChannelIds!.length === 0
            ? zeroCounts
            : await channelConversationService.getConversationCounts({
                ...sharedFilters,
                channelIds: connectedChannelIds!,
              })
      }

      const subtractCounts = (total: typeof zeroCounts, part: typeof zeroCounts) => ({
        waiting: Math.max(0, total.waiting - part.waiting),
        active: Math.max(0, total.active - part.active),
        closed: Math.max(0, total.closed - part.closed),
        all: Math.max(0, total.all - part.all),
      })

      // When showing disconnected channels (or the connected set is unknown),
      // the visible list spans every channel, so stats is the total. Otherwise
      // it is the connected-only count. Disconnected = total minus connected.
      const stats = !channelsKnown || showDisconnectedChannels ? totalCounts : connectedCounts
      const disconnectedCountByStatus = channelsKnown
        ? subtractCounts(totalCounts, connectedCounts)
        : zeroCounts

      // Don't update chatsByStatus here to avoid overwriting pagination state of current view
      set({ stats, disconnectedCountByStatus })
    } catch (error) {
      console.error("Failed to sync counts", error)
    }
  },

  reconcileAfterReconnect: async () => {
    // Ably auto-recovers messages missed during a brief disconnect (within the
    // connection state TTL). This runs only after a `suspended` gap, where that
    // continuity is lost and live events were dropped. It reconciles silently:
    // no loading spinners, no error toasts, and it never wipes loaded messages
    // or resets the open conversation's scroll position.
    const {
      activeChat,
      statusFilter,
      tagsInclude,
      tagsExclude,
      groupFilter,
      channelFilter,
      assigneeFilter,
      showDisconnectedChannels,
      connectedChannelIds,
    } = get()

    const { ids, blockAll } = resolveFetchChannelIds(
      channelFilter,
      showDisconnectedChannels,
      connectedChannelIds
    )

    try {
      const isGroupParam =
        groupFilter === "group" ? true : groupFilter === "direct" ? false : undefined
      const limit = 20
      const conversations = blockAll
        ? []
        : await channelConversationService.getConversations(
            statusFilter || "all",
            limit,
            0,
            undefined,
            tagsInclude.length ? tagsInclude : undefined,
            tagsExclude.length ? tagsExclude : undefined,
            isGroupParam,
            ids?.length ? ids : undefined,
            assigneeFilter.attendantIds.length ? assigneeFilter.attendantIds : undefined,
            assigneeFilter.includeUnassigned || undefined,
            assigneeFilter.assignedToMe || undefined
          )

      if (!blockAll && Array.isArray(conversations)) {
        const fetched = conversations.map(mapApiConversationToChat)
        set(state => {
          const existingById = new Map(state.chats.map(chat => [chat.id, chat]))
          const merged = fetched.map(fresh => {
            const existing = existingById.get(fresh.id)
            if (!existing) return fresh
            // Refresh metadata from the server but keep already-loaded messages.
            // The active chat stays read since the user is looking at it.
            return {
              ...fresh,
              messages: existing.messages,
              unreadCount: fresh.id === activeChat ? 0 : fresh.unreadCount,
            }
          })
          // Keep loaded chats that fell outside this page (e.g. the open one
          // further down the list) so reconciliation never drops them.
          const mergedIds = new Set(merged.map(chat => chat.id))
          const preserved = state.chats.filter(chat => !mergedIds.has(chat.id))
          const all = [...merged, ...preserved].sort((a, b) => {
            const aTime = a.lastMessage?.timestamp.getTime() || 0
            const bTime = b.lastMessage?.timestamp.getTime() || 0
            return bTime - aTime
          })
          return {
            chats: all,
            chatsByStatus: { ...state.chatsByStatus, [statusFilter || "all"]: all },
          }
        })
      }
    } catch (error) {
      console.error("Failed to reconcile conversations after reconnect", error)
    }

    if (activeChat) {
      try {
        const MESSAGE_LIMIT = 50
        const apiMessages = await channelConversationService.getMessages(activeChat, MESSAGE_LIMIT)
        if (Array.isArray(apiMessages)) {
          const mappedMessages = apiMessages.map(mapApiMessageToMessage)
          const resolved = resolveQuotedMessages(mappedMessages, apiMessages)
          set(state => ({
            chats: state.chats.map(chat => {
              if (chat.id !== activeChat) return chat
              const existingIds = new Set(chat.messages.map(message => message.id))
              const missing = resolved.filter(message => !existingIds.has(message.id))
              if (missing.length === 0) return chat
              const messages = [...chat.messages, ...missing].sort(
                (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
              )
              return { ...chat, messages, lastMessage: messages[messages.length - 1] }
            }),
          }))
        }
      } catch (error) {
        console.error("Failed to reconcile active conversation after reconnect", error)
      }
    }

    get().syncCounts()
  },

  fetchConversation: async (conversationId: string) => {
    const conversation = await channelConversationService.getConversation(conversationId)
    // Handoff info now comes directly from the API response
    return mapApiConversationToChat(conversation)
  },

  fetchMessages: async conversationId => {
    try {
      set({ messagesLoading: true, messagesError: null })

      const MESSAGE_LIMIT = 50
      const apiMessages = await channelConversationService.getMessages(
        conversationId,
        MESSAGE_LIMIT
      )

      if (Array.isArray(apiMessages)) {
        const mappedMessages = apiMessages.map(mapApiMessageToMessage)
        const messages = resolveQuotedMessages(mappedMessages, apiMessages)

        set(state => ({
          chats: state.chats.map(chat =>
            chat.id === conversationId ? { ...chat, messages } : chat
          ),
          hasMoreMessages: {
            ...state.hasMoreMessages,
            [conversationId]: apiMessages.length >= MESSAGE_LIMIT,
          },
        }))

        // Fetch feedbacks for AI messages in batch
        const aiMessageIds = messages.filter(m => m.isAIMessage).map(m => m.id)
        if (aiMessageIds.length > 0) {
          get().fetchFeedbacks(aiMessageIds)
        }
      }
    } catch (error) {
      console.error(error)
      const errorMessage = extractErrorMessage(error, "Falha ao buscar mensagens")
      set({ messagesError: errorMessage })
    } finally {
      set({ messagesLoading: false })
    }
  },

  sendMessage: (chatId, content, options = {}) =>
    set(state => {
      const now = new Date()
      const messageContent =
        typeof content === "string"
          ? content
          : content.type === "text"
            ? content.text
            : content.type === "template"
              ? (content as { body_preview?: string }).body_preview ||
                `Template: ${(content as { template_name: string }).template_name}`
              : ""
      const replyToMessage = options.replyTo
        ? state.chats
            .find(chat => chat.id === chatId)
            ?.messages.find(msg => msg.id === options.replyTo)
        : undefined

      // Build a media payload for image/video/audio/document so the chat UI
      // renders the optimistic message right away instead of dropping it
      // (the media-type components return null when message.media is missing).
      const mediaTypes = ["image", "video", "audio", "myaudio", "document", "sticker"] as const
      const optimisticMedia: MessageMedia | undefined = (() => {
        if (typeof content === "string") return undefined
        const c = content as {
          type: string
          url?: string
          caption?: string
          filename?: string
          mimeType?: string
        }
        if (!c.url || !(mediaTypes as readonly string[]).includes(c.type)) return undefined
        return {
          url: c.url,
          type: c.type as MessageMedia["type"],
          caption: c.caption,
          filename: c.filename,
          mimeType: c.mimeType,
        }
      })()

      const isInternalNote = options.isInternalNote === true
      const authUser = isInternalNote ? authStore.getState().user : undefined
      const noteAuthorName = authUser?.name
      const noteAuthorId = authUser?.id
      const mentionedUserIds = options.mentionedUserIds?.length
        ? options.mentionedUserIds
        : undefined

      const newMessage: Message = {
        id: `msg_${Date.now()}`,
        content: messageContent,
        formatted_content: messageContent,
        sender: "user",
        timestamp: now,
        status: "sent",
        type: isInternalNote ? "internal_note" : (content.type as MessageType),
        ...(isInternalNote && {
          isInternalNote: true,
          ...(noteAuthorId ? { authorId: noteAuthorId } : {}),
          ...(noteAuthorName || mentionedUserIds
            ? {
                metadata: {
                  ...(noteAuthorName ? { sender_name: noteAuthorName } : {}),
                  ...(mentionedUserIds ? { mentioned_user_ids: mentionedUserIds } : {}),
                },
              }
            : {}),
        }),
        ...(optimisticMedia && { media: optimisticMedia }),
        ...(replyToMessage && {
          replyTo: {
            id: replyToMessage.id,
            content: replyToMessage.content,
            formatted_content: replyToMessage.formatted_content,
            sender: replyToMessage.sender,
            type: replyToMessage.type,
            media: replyToMessage.media,
          },
        }),
        ...(options.forwarded && { forwarded: options.forwarded }),
        ...(options.forwardedFrom && { forwardedFrom: options.forwardedFrom }),
      }

      // Async call to API
      channelConversationService
        .sendMessage(chatId, content, options)
        .then(response => {
          // Internal notes are attendant-only and never reach the customer, so
          // they must not fire the human-message analytics.
          if (!isInternalNote) {
            trackEvent("Human Message Sent", { conversation_id: chatId })
          }
          // Replace the optimistic message with the server-mapped one so the
          // media URL points at the persisted S3 URL instead of the base64
          // data URL we used for the immediate preview.
          if (response?.id) {
            const mapped = mapApiMessageToMessage(response as ApiMessage)
            set(state => ({
              chats: state.chats.map(chat => {
                if (chat.id !== chatId) return chat
                return {
                  ...chat,
                  messages: chat.messages.map(msg => (msg.id === newMessage.id ? mapped : msg)),
                }
              }),
            }))
          }
        })
        .catch(error => {
          console.error(error)
          // Drop the optimistic message and surface the failure so users
          // don't see a phantom "sent" bubble.
          set(state => ({
            chats: state.chats.map(chat =>
              chat.id === chatId
                ? {
                    ...chat,
                    messages: chat.messages.filter(msg => msg.id !== newMessage.id),
                  }
                : chat
            ),
          }))
          const errorCode =
            error instanceof AxiosError
              ? (error.response?.data as { code?: string } | undefined)?.code
              : undefined
          if (errorCode === "messaging_window_closed") {
            toast.error(
              "A janela de 24h da conversa expirou. Envie uma mensagem template aprovada para reabrir."
            )
            return
          }
          toast.error(extractErrorMessage(error, "Não foi possível enviar a mensagem"))
        })

      return {
        chats: state.chats.map(chat =>
          chat.id === chatId
            ? {
                ...chat,
                messages: [...chat.messages, newMessage],
                // A note must not overwrite the conversation preview, so keep
                // the existing last_message untouched for internal notes.
                ...(isInternalNote ? {} : { lastMessage: newMessage }),
              }
            : chat
        ),
      }
    }),

  editMessage: async (messageId, text, mentionedUserIds) => {
    const chats = get().chats
    const targetChat = chats.find(chat => chat.messages.some(msg => msg.id === messageId))
    const previous = targetChat?.messages.find(msg => msg.id === messageId)
    if (!targetChat || !previous) return

    const editedAt = new Date().toISOString()
    const cleanMentions = mentionedUserIds?.length ? mentionedUserIds : undefined

    // Optimistic: reflect the new text and edited marker right away; the
    // realtime message.update reconciles with the server-recorded edit history.
    set(state => ({
      chats: state.chats.map(chat =>
        chat.id === targetChat.id
          ? {
              ...chat,
              messages: chat.messages.map(msg =>
                msg.id === messageId
                  ? {
                      ...msg,
                      content: text,
                      formatted_content: text,
                      metadata: {
                        ...msg.metadata,
                        edited_at: editedAt,
                        mentioned_user_ids: cleanMentions,
                      },
                    }
                  : msg
              ),
            }
          : chat
      ),
    }))

    try {
      await channelConversationService.editMessage(messageId, text, cleanMentions)
    } catch (error) {
      // Revert to the pre-edit message so the timeline never shows a phantom edit.
      set(state => ({
        chats: state.chats.map(chat =>
          chat.id === targetChat.id
            ? {
                ...chat,
                messages: chat.messages.map(msg => (msg.id === messageId ? previous : msg)),
              }
            : chat
        ),
      }))
      toast.error(extractErrorMessage(error, "Não foi possível editar a nota"))
    }
  },

  deleteMessage: async messageId => {
    const chats = get().chats
    const targetChat = chats.find(chat => chat.messages.some(msg => msg.id === messageId))
    const previous = targetChat?.messages.find(msg => msg.id === messageId)
    if (!targetChat || !previous) return

    const deletedAt = new Date().toISOString()

    // Optimistic soft-delete: flip the tombstone flags; content is kept for audit.
    set(state => ({
      chats: state.chats.map(chat =>
        chat.id === targetChat.id
          ? {
              ...chat,
              messages: chat.messages.map(msg =>
                msg.id === messageId
                  ? {
                      ...msg,
                      metadata: { ...msg.metadata, deleted: true, deleted_at: deletedAt },
                    }
                  : msg
              ),
            }
          : chat
      ),
    }))

    try {
      await channelConversationService.deleteMessage(messageId)
    } catch (error) {
      set(state => ({
        chats: state.chats.map(chat =>
          chat.id === targetChat.id
            ? {
                ...chat,
                messages: chat.messages.map(msg => (msg.id === messageId ? previous : msg)),
              }
            : chat
        ),
      }))
      toast.error(extractErrorMessage(error, "Não foi possível excluir a nota"))
    }
  },

  toggleReaction: async (messageId, emoji, userId) => {
    const chats = get().chats
    const targetChat = chats.find(chat => chat.messages.some(msg => msg.id === messageId))
    const previous = targetChat?.messages.find(msg => msg.id === messageId)
    if (!targetChat || !previous) return

    const nextReactions = toggleNoteReaction(
      toNoteReactions(previous.metadata?.reactions),
      emoji,
      userId
    )

    // Optimistic: reflect the toggled reaction right away; the realtime
    // message.update reconciles with the server-recorded reactions.
    set(state => ({
      chats: state.chats.map(chat =>
        chat.id === targetChat.id
          ? {
              ...chat,
              messages: chat.messages.map(msg =>
                msg.id === messageId
                  ? { ...msg, metadata: { ...msg.metadata, reactions: nextReactions } }
                  : msg
              ),
            }
          : chat
      ),
    }))

    try {
      await channelConversationService.reactToMessage(messageId, emoji)
    } catch (error) {
      set(state => ({
        chats: state.chats.map(chat =>
          chat.id === targetChat.id
            ? {
                ...chat,
                messages: chat.messages.map(msg => (msg.id === messageId ? previous : msg)),
              }
            : chat
        ),
      }))
      toast.error(extractErrorMessage(error, "Não foi possível reagir à nota"))
    }
  },

  markAsRead: async chatId => {
    try {
      // Update local state immediately for better UX
      set(state => ({
        chats: state.chats.map(chat => (chat.id === chatId ? { ...chat, unreadCount: 0 } : chat)),
      }))

      // Call API to update server state
      await channelConversationService.markAsRead(chatId)
    } catch (error) {
      console.error(`Failed to mark conversation ${chatId} as read:`, error)
      // Optionally revert the state change on error
    }
  },

  handleNewMessage: (data: MessageNotificationDTO) => {
    const { conversation, message } = data
    if (!message) return

    // Mapear a mensagem recebida para o formato interno
    let newMessage = mapMessageNotificationToMessage(message)

    // Resolve replyTo if quoted_message_id is present and the quoted message is already loaded
    if (message.quoted_message_id) {
      const chat = get().chats.find(c => c.id === conversation.id)
      const quoted = chat?.messages.find(m => m.id === message.quoted_message_id)
      if (quoted) {
        newMessage = {
          ...newMessage,
          replyTo: {
            id: quoted.id,
            content: quoted.content,
            formatted_content: quoted.formatted_content,
            sender: quoted.sender,
            type: quoted.type,
            media: quoted.media,
          },
        }
      }
    }

    // Verificar se já temos esta conversa carregada
    const chatExists = get().chats.some(chat => chat.id === conversation.id)

    if (chatExists) {
      set(state => {
        let chatChanged = false
        const updatedChats = state.chats.map(chat => {
          if (chat.id !== conversation.id) return chat

          const messageExists = chat.messages.some(msg => msg.id === newMessage.id)
          if (messageExists) return chat

          chatChanged = true
          // Internal notes are attendant-only: they never reopen the messaging
          // window, never bump unread, and never overwrite the conversation
          // preview, so append them without any of the outbound-send effects.
          if (newMessage.isInternalNote) {
            return { ...chat, messages: [...chat.messages, newMessage] }
          }
          // When the contact replies, the Meta 24h messaging window reopens.
          // Advance optimistically so the UI unblocks immediately; the backend
          // still validates on send.
          const reopenedWindow =
            message.direction === MessageDirection.INBOUND
              ? new Date(newMessage.timestamp.getTime() + 24 * 60 * 60 * 1000)
              : chat.messagingWindowExpiresAt
          return {
            ...chat,
            messages: [...chat.messages, newMessage],
            lastMessage: newMessage,
            unreadCount: state.activeChat === chat.id ? 0 : chat.unreadCount + 1,
            messagingWindowExpiresAt: reopenedWindow,
          }
        })

        if (!chatChanged) return state

        // Move the updated chat to the top instead of sorting the entire array.
        // A note leaves the preview untouched, so it must not reorder the list.
        if (!newMessage.isInternalNote) {
          const chatIndex = updatedChats.findIndex(c => c.id === conversation.id)
          if (chatIndex > 0) {
            const [chat] = updatedChats.splice(chatIndex, 1)
            updatedChats.unshift(chat)
          }
        }

        return { chats: updatedChats }
      })
    } else {
      // Instead of refetching all conversations (which wipes messages from loaded chats),
      // fetch only this specific conversation and prepend it if it matches the current filter
      get()
        .fetchConversation(conversation.id)
        .then(chat => {
          const { statusFilter, chats } = get()
          // Only add to the list if it matches the current filter or filter is "all"
          if (statusFilter === "all" || chat.status === statusFilter) {
            // Avoid duplicates (in case it was added between the check and the fetch)
            if (!chats.some(c => c.id === chat.id)) {
              // Add the new message to the fetched chat. Internal notes are
              // attendant-only, so they never bump unread nor overwrite the
              // conversation preview: keep the fetched chat's own last message.
              const chatWithMessage = newMessage.isInternalNote
                ? {
                    ...chat,
                    messages: [...chat.messages, newMessage],
                  }
                : {
                    ...chat,
                    messages: [newMessage],
                    lastMessage: newMessage,
                    unreadCount: 1,
                  }
              set(state => ({ chats: [chatWithMessage, ...state.chats] }))
            }
          }
        })
        .catch(err => {
          console.error("Failed to fetch new conversation:", err)
        })

      // Sync counts in background since a new conversation appeared
      get().syncCounts()
    }
  },

  applyConversationStatusChange: (conversationId, newStatus, patch = {}) => {
    const { statusFilter, chats } = get()
    const matchesCurrentTab = statusFilter === "all" || newStatus === statusFilter
    const existing = chats.some(chat => chat.id === conversationId)

    if (existing) {
      set(state => {
        const keepVisible =
          state.statusFilter === "all" ||
          newStatus === state.statusFilter ||
          state.activeChat === conversationId
        return {
          chats: keepVisible
            ? state.chats.map(chat =>
                chat.id === conversationId ? { ...chat, ...patch, status: newStatus } : chat
              )
            : state.chats.filter(chat => chat.id !== conversationId),
        }
      })
    } else if (matchesCurrentTab) {
      // The conversation just moved INTO the current tab (e.g. a handoff
      // arriving while the operator watches Pendentes): fetch and prepend it.
      get()
        .fetchConversation(conversationId)
        .then(chat => {
          const { statusFilter: currentFilter, chats: currentChats } = get()
          const stillMatches = currentFilter === "all" || chat.status === currentFilter
          if (stillMatches && !currentChats.some(c => c.id === chat.id)) {
            set(state => ({ chats: [chat, ...state.chats] }))
          }
        })
        .catch(err => console.error("Failed to fetch conversation after status change:", err))
    }

    get().syncCounts()
  },

  // Método para processar eventos de handoff do WebSocket (Ably)
  handleHandoffUpdate: (data: HandoffNotificationDTO) => {
    const { conversation_id, handoff_until_date, handoff_reason, is_active } = data
    const newStatus = data.status ?? (is_active ? "waiting" : "active")

    get().applyConversationStatusChange(conversation_id, newStatus, {
      handoffUntilDate: is_active && handoff_until_date ? new Date(handoff_until_date) : undefined,
      handoffReason: is_active ? (handoff_reason ?? undefined) : undefined,
      isAIHandled: !is_active,
    })
  },

  // Processa o evento conversation.update (payload plano do backend)
  handleConversationUpdate: (data: ConversationUpdateNotificationDTO) => {
    if (!data?.id || !data.status) return

    const patch: Partial<Chat> = {}
    if (data.attendant_id !== undefined) {
      patch.attendantId = data.attendant_id
    }
    get().applyConversationStatusChange(data.id, data.status, patch)
  },

  // Método para encerrar o handoff de uma conversa
  endHandoff: async (conversationId: string) => {
    try {
      // Call API to end handoff
      const updatedConversation = await channelConversationService.endHandoff(conversationId)

      // Patch without messages so the loaded history is never wiped, then let
      // the shared transition handler move the chat between tabs and re-count.
      const { messages: _ignoredMessages, ...patch } = mapApiConversationToChat(updatedConversation)
      get().applyConversationStatusChange(conversationId, updatedConversation.status, patch)
    } catch (error) {
      console.error(`Failed to end handoff for conversation ${conversationId}:`, error)
      throw error
    }
  },

  // Método para processar eventos do WebSocket
  handleWebSocketEvent: (event: WebSocketEvent) => {
    console.log("handleWebSocketEvent chat store", event)
    const state = get()

    switch (event.type) {
      case "message.new": {
        const { conversation, message } = event.data

        if (!message) return

        // Mapear a mensagem recebida para o formato interno
        const newMessage = mapApiMessageToMessage(message)

        // Verificar se já temos esta conversa carregada
        const chatExists = state.chats.some(chat => chat.id === conversation.id)

        if (chatExists) {
          // Atualizar a conversa existente e reordenar
          set(state => {
            // Primeiro atualizar as mensagens e contadores
            const updatedChats = state.chats.map(chat => {
              if (chat.id !== conversation.id) return chat

              // Evitar duplicação de mensagens
              const messageExists = chat.messages.some(msg => msg.id === newMessage.id)
              if (messageExists) return chat

              // Se for a conversa certa, adicionar a mensagem
              return {
                ...chat,
                messages: [...chat.messages, newMessage],
                lastMessage: newMessage,
                unreadCount: state.activeChat === chat.id ? 0 : chat.unreadCount + 1,
              }
            })

            // Depois reordenar para trazer a conversa atualizada para o topo
            return {
              chats: updatedChats.sort((a, b) => {
                // Ordenar pelo timestamp da última mensagem (mais recente primeiro)
                const aTime = a.lastMessage?.timestamp.getTime() || 0
                const bTime = b.lastMessage?.timestamp.getTime() || 0
                return bTime - aTime
              }),
            }
          })
          // Handoff status is now included in the conversation data from the API
          // No separate refresh needed
        } else {
          // Fetch only this conversation instead of refetching all (which wipes messages)
          state
            .fetchConversation(conversation.id)
            .then(chat => {
              const { statusFilter, chats } = get()
              if (statusFilter === "all" || chat.status === statusFilter) {
                if (!chats.some(c => c.id === chat.id)) {
                  const chatWithMessage = {
                    ...chat,
                    messages: [newMessage],
                    lastMessage: newMessage,
                    unreadCount: 1,
                  }
                  set(s => ({ chats: [chatWithMessage, ...s.chats] }))
                }
              }
            })
            .catch(err => console.error("Failed to fetch new conversation:", err))
          state.syncCounts()
        }
        break
      }

      case "message.update": {
        const data = event.data
        // Support both formats: {message_id, updates} (reactions/metadata) and {conversation_id, message} (status)
        const messageId = data.message_id || data.message?.id
        const conversationId =
          data.updates?.conversation_id || data.conversation_id || data.message?.conversation_id
        const updates = data.updates || {}
        const status = updates.status || data.message?.status
        const reactions = updates.reactions || data.message?.reactions
        // NEW: backend's notify_message_update on edit/revoke sends the full
        // metadata + content payload. Merge metadata so flags like deleted /
        // edited_at / edit_history land on the message; replace content so
        // edited text shows immediately.
        const updatedContent = updates.content ?? data.message?.content
        const updatedMetadata = updates.metadata ?? data.message?.metadata
        // Pipeline-status push (pipeline_status_enabled). Fold the run status
        // and failed step into message metadata so the bubble can surface it.
        const pipelineStatus = updates.pipeline_status
        const pipelineRunId = updates.pipeline_run_id
        const failedStep = updates.failed_step
        const pipelineMetadata =
          pipelineStatus !== undefined
            ? {
                pipeline_status: pipelineStatus,
                pipeline_run_id: pipelineRunId,
                failed_step: failedStep,
              }
            : undefined

        if (!messageId) return

        set(state => ({
          chats: state.chats.map(chat => {
            if (chat.id !== conversationId) return chat

            return {
              ...chat,
              messages: chat.messages.map(msg => {
                if (msg.id !== messageId) return msg

                const mergedMetadata = {
                  ...msg.metadata,
                  ...(updatedMetadata ?? {}),
                  ...(reactions && !updatedMetadata ? { reactions } : {}),
                  ...(pipelineMetadata ?? {}),
                }

                return {
                  ...msg,
                  ...(status
                    ? {
                        status: status as "sent" | "delivered" | "read" | "pending" | "failed",
                      }
                    : {}),
                  ...(updatedContent !== undefined ? { content: updatedContent } : {}),
                  metadata: mergedMetadata,
                }
              }),
            }
          }),
        }))
        break
      }

      case "history.sync_complete": {
        const { conversation_id, messages, has_more, chunk_index, total_chunks, is_last } =
          event.data || {}
        if (!conversation_id || !Array.isArray(messages)) break

        // Backend chunks large histories to stay under Ably's 64KB limit
        // (SPEEKE-API-X4). Accumulate chunks in a module-local buffer and
        // commit to store state only when the last chunk arrives. Single-
        // chunk payloads (default for small histories) take the
        // commit-immediately path: total_chunks is 1 or undefined.
        const totalChunks = typeof total_chunks === "number" && total_chunks > 0 ? total_chunks : 1
        const chunkIndex = typeof chunk_index === "number" ? chunk_index : 0
        const isLast = typeof is_last === "boolean" ? is_last : true

        const buffer = historySyncChunkBuffer.get(conversation_id) ?? []
        buffer[chunkIndex] = messages
        historySyncChunkBuffer.set(conversation_id, buffer)

        if (!isLast) break

        // Last chunk arrived - stitch in delivery order. Any missing
        // slot (out-of-order or dropped chunk) falls back to []; the
        // worst case is a partial history that the user can refresh,
        // which beats committing inconsistent state.
        const orderedRawMessages: ApiMessage[] = []
        for (let i = 0; i < totalChunks; i++) {
          const chunk = buffer[i] ?? []
          orderedRawMessages.push(...chunk)
        }
        historySyncChunkBuffer.delete(conversation_id)

        const mappedMessages = orderedRawMessages.map(mapApiMessageToMessage)
        const olderMessages = resolveQuotedMessages(mappedMessages, orderedRawMessages)

        set(state => ({
          chats: state.chats.map(chat =>
            chat.id === conversation_id
              ? { ...chat, messages: [...olderMessages, ...chat.messages] }
              : chat
          ),
          hasMoreMessages: {
            ...state.hasMoreMessages,
            [conversation_id]: has_more,
          },
          historySyncStatus: {
            ...state.historySyncStatus,
            [conversation_id]: has_more ? "idle" : "no_more",
          },
        }))
        break
      }

      case "conversation.update": {
        // Atualiza informações da conversa (ex: online status, isAIHandled, etc)
        const { conversation } = event.data

        if (!conversation) return

        set(state => ({
          chats: state.chats.map(chat => {
            if (chat.id !== conversation.id) return chat

            return {
              ...chat,
              ...mapApiConversationToChat(conversation),
              // Manter as mensagens que já tínhamos carregadas
              messages: chat.messages,
            }
          }),
        }))
        break
      }
    }
  },

  fetchContact: async (contactId: string) => {
    try {
      set({ contactLoading: true, contactError: null })
      const contact = await contactService.getContact(contactId)
      set({ contact, contactLoading: false })
    } catch (error) {
      console.error(error)
      const errorMessage = extractErrorMessage(error, "Falha ao buscar informações do contato")
      set({ contactError: errorMessage, contactLoading: false })
    }
  },

  fetchLead: async (leadId: string) => {
    if (!leadId) {
      set({ leadError: "ID do Lead inválido.", lead: null, leadLoading: false })
      return
    }
    // Hard sync between the conversation header/list and the lead panel: capture
    // the conversation this lead is being fetched for. If the operator switches
    // conversations while the request is in flight, a slower response from the
    // previous conversation must never overwrite the now-active one's identity.
    const requestedChatId = get().activeChat
    const isStillForRequestedChat = () => {
      const state = get()
      if (state.activeChat !== requestedChatId) return false
      const activeChat = state.chats.find(chat => chat.id === state.activeChat)
      return activeChat?.leadId === leadId
    }
    try {
      set({ leadLoading: true, leadError: null, lead: null })
      const leadData = await leadService.getLead(leadId)
      if (!isStillForRequestedChat()) return
      set({ lead: leadData, leadLoading: false })

      // Keep the conversation's display name as the WhatsApp contact name; the
      // lead/CRM name is surfaced separately (header subtitle) so the two
      // identities never overwrite each other. Only mirror the lead photo, and
      // only onto the conversation this fetch was for (never whichever chat
      // merely happens to be active when this resolves).
      if (requestedChatId && leadData.photo_url) {
        set(state => ({
          chats: state.chats.map(chat =>
            chat.id === requestedChatId
              ? { ...chat, imageUrl: leadData.photo_url || chat.imageUrl }
              : chat
          ),
        }))
      }
    } catch (error) {
      if (!isStillForRequestedChat()) return
      console.error(`Failed to fetch lead ${leadId}: `, error)
      const errorMessage = extractErrorMessage(error, "Falha ao buscar informações do lead")
      set({ leadError: errorMessage, lead: null, leadLoading: false })
    }
  },

  markConversationAsClosed: async (conversationId: string) => {
    try {
      // Update local state immediately for better UX
      set(state => ({
        chats: state.chats.map(chat =>
          chat.id === conversationId ? { ...chat, status: "closed" } : chat
        ),
      }))

      // Call action to update server state
      await markConversationAsClosed(conversationId)

      // Optionally refetch conversations to sync with server
      await get().fetchConversations(get().statusFilter)
      get().syncCounts()
    } catch (error) {
      console.error(`Failed to mark conversation ${conversationId} as closed:`, error)
      // Revert the state change on error
      await get().fetchConversations(get().statusFilter)
      throw error
    }
  },

  fetchFeedbacks: async (messageIds: string[]) => {
    try {
      const feedbacks = await feedbackService.getBatchFeedback(messageIds)
      const map: Record<string, FeedbackResponse> = {}
      for (const fb of feedbacks) {
        map[fb.message_id] = fb
      }
      set(state => ({
        feedbacksByMessageId: { ...state.feedbacksByMessageId, ...map },
      }))
    } catch (err) {
      console.error("Failed to fetch batch feedbacks:", err)
    }
  },

  loadMoreMessages: async (conversationId: string) => {
    const { loadingMoreMessages, hasMoreMessages, chats } = get()
    if (loadingMoreMessages || !hasMoreMessages[conversationId]) return

    const chat = chats.find(c => c.id === conversationId)
    if (!chat || chat.messages.length === 0) return

    try {
      set({ loadingMoreMessages: true })

      const oldestMessageId = chat.messages[0].id
      const MESSAGE_LIMIT = 50
      const apiMessages = await channelConversationService.getMessages(
        conversationId,
        MESSAGE_LIMIT,
        oldestMessageId
      )

      if (Array.isArray(apiMessages) && apiMessages.length > 0) {
        const mappedMessages = apiMessages.map(mapApiMessageToMessage)
        const olderMessages = resolveQuotedMessages(mappedMessages, apiMessages)

        set(state => ({
          chats: state.chats.map(c =>
            c.id === conversationId ? { ...c, messages: [...olderMessages, ...c.messages] } : c
          ),
          hasMoreMessages: {
            ...state.hasMoreMessages,
            [conversationId]: apiMessages.length >= MESSAGE_LIMIT,
          },
        }))

        // Fetch feedbacks for AI messages in the older batch
        const aiMessageIds = olderMessages.filter(m => m.isAIMessage).map(m => m.id)
        if (aiMessageIds.length > 0) {
          get().fetchFeedbacks(aiMessageIds)
        }
      } else {
        set(state => ({
          hasMoreMessages: { ...state.hasMoreMessages, [conversationId]: false },
        }))
      }
    } catch (error) {
      console.error("Failed to load more messages:", error)
    } finally {
      set({ loadingMoreMessages: false })
    }
  },

  requestWhatsAppHistory: async (conversationId: string) => {
    const currentStatus = get().historySyncStatus[conversationId]
    if (currentStatus === "requesting" || currentStatus === "waiting") return

    try {
      set(state => ({
        historySyncStatus: { ...state.historySyncStatus, [conversationId]: "requesting" },
      }))

      await channelConversationService.requestHistory(conversationId)

      set(state => ({
        historySyncStatus: { ...state.historySyncStatus, [conversationId]: "waiting" },
      }))

      // Poll status after 30s - UaZapi times out at 25s
      setTimeout(() => {
        const status = get().historySyncStatus[conversationId]
        if (status === "waiting") {
          get().fetchHistorySyncStatus(conversationId)
        }
      }, 30000)
    } catch (error) {
      console.error("Failed to request WhatsApp history:", error)
      set(state => ({
        historySyncStatus: { ...state.historySyncStatus, [conversationId]: "idle" },
      }))
    }
  },

  fetchHistorySyncStatus: async (conversationId: string) => {
    try {
      const data = await channelConversationService.getHistorySyncStatus(conversationId)
      if (data.status) {
        const statusMap: Record<string, typeof data.status> = {
          requested: "waiting",
          completed: "idle",
          timeout: "timeout",
          no_more: "no_more",
        }
        const mapped = statusMap[data.status] || "idle"
        set(state => ({
          historySyncStatus: {
            ...state.historySyncStatus,
            [conversationId]: mapped as
              | "idle"
              | "requesting"
              | "waiting"
              | "completed"
              | "timeout"
              | "no_more",
          },
        }))
      }
    } catch {
      // Silently ignore - status not critical
    }
  },

  searchConversations: async (query: string) => {
    try {
      set({ searchLoading: true })
      const {
        tagsInclude,
        tagsExclude,
        channelFilter,
        assigneeFilter,
        showDisconnectedChannels,
        connectedChannelIds,
      } = get()
      const { ids, blockAll } = resolveFetchChannelIds(
        channelFilter,
        showDisconnectedChannels,
        connectedChannelIds
      )
      if (blockAll) {
        set({ searchResults: [] })
        return
      }
      const conversations = await channelConversationService.getConversations(
        "all",
        20,
        0,
        query,
        tagsInclude.length ? tagsInclude : undefined,
        tagsExclude.length ? tagsExclude : undefined,
        undefined,
        ids?.length ? ids : undefined,
        assigneeFilter.attendantIds.length ? assigneeFilter.attendantIds : undefined,
        assigneeFilter.includeUnassigned || undefined,
        assigneeFilter.assignedToMe || undefined
      )
      if (Array.isArray(conversations)) {
        set({ searchResults: conversations.map(mapApiConversationToChat) })
      }
    } catch (error) {
      console.error("Failed to search conversations:", error)
      set({ searchResults: [] })
    } finally {
      set({ searchLoading: false })
    }
  },

  clearSearch: () => {
    set({ searchResults: null, searchLoading: false })
  },

  setFeedback: (messageId: string, feedback: FeedbackResponse | null) => {
    set(state => {
      const updated = { ...state.feedbacksByMessageId }
      if (feedback) {
        updated[messageId] = feedback
      } else {
        delete updated[messageId]
      }
      return { feedbacksByMessageId: updated }
    })
  },

  addTagInclude: async (tag: string) => {
    const current = get().tagsInclude
    if (current.includes(tag)) return
    set({ tagsInclude: [...current, tag], chats: [], hasMore: true })
    await get().fetchConversations(get().statusFilter)
    get().syncCounts()
  },

  removeTagInclude: async (tag: string) => {
    set(state => ({
      tagsInclude: state.tagsInclude.filter(t => t !== tag),
      chats: [],
      hasMore: true,
    }))
    await get().fetchConversations(get().statusFilter)
    get().syncCounts()
  },

  addTagExclude: async (tag: string) => {
    const current = get().tagsExclude
    if (current.includes(tag)) return
    set({ tagsExclude: [...current, tag], chats: [], hasMore: true })
    await get().fetchConversations(get().statusFilter)
    get().syncCounts()
  },

  removeTagExclude: async (tag: string) => {
    set(state => ({
      tagsExclude: state.tagsExclude.filter(t => t !== tag),
      chats: [],
      hasMore: true,
    }))
    await get().fetchConversations(get().statusFilter)
    get().syncCounts()
  },

  clearTagFilters: async () => {
    set({ tagsInclude: [], tagsExclude: [], channelFilter: [], chats: [], hasMore: true })
    await get().fetchConversations(get().statusFilter)
    get().syncCounts()
  },

  setGroupFilter: async filter => {
    set({ groupFilter: filter, chats: [], hasMore: true })
    await get().fetchConversations(get().statusFilter)
    get().syncCounts()
  },

  setChannelFilter: async channelIds => {
    set({ channelFilter: channelIds, chats: [], hasMore: true })
    await get().fetchConversations(get().statusFilter)
    get().syncCounts()
  },

  setShowDisconnectedChannels: async value => {
    persistShowDisconnectedChannels(value)
    set({ showDisconnectedChannels: value, chats: [], hasMore: true })
    await get().fetchConversations(get().statusFilter)
    await get().syncCounts()
  },

  setConnectedChannelIds: async channelIds => {
    const previous = get().connectedChannelIds
    const unchanged =
      previous !== null &&
      previous.length === channelIds.length &&
      channelIds.every(id => previous.includes(id))
    set({ connectedChannelIds: channelIds })
    // Recompute counts now that channel connectivity is known.
    await get().syncCounts()
    // Refetch the visible list only when the connected set actually changed and
    // the user is not in an active search (which manages its own results).
    if (!unchanged && get().searchResults === null) {
      await get().fetchConversations(get().statusFilter)
    }
  },

  setAssigneeFilter: async filter => {
    set({ assigneeFilter: filter, chats: [], hasMore: true })
    await get().fetchConversations(get().statusFilter)
    get().syncCounts()
  },

  assignAttendant: async (conversationId, attendantId) => {
    const updated = await channelConversationService.assignAttendant(conversationId, attendantId)
    // Assigning pauses the AI and moves the conversation to Pendentes on the
    // backend, so run the full tab-move + badge re-count transition.
    get().applyConversationStatusChange(conversationId, updated.status ?? "waiting", {
      attendantId: updated.attendantId ?? null,
    })
  },

  updateChatTags: (leadId: string, tags: string[]) => {
    set(state => ({
      chats: state.chats.map(chat => (chat.leadId === leadId ? { ...chat, tags } : chat)),
    }))
  },
}))
